import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { SessionStorageService } from 'ngx-webstorage';
import { lastValueFrom, Observable } from 'rxjs';

import { completeEndpoint, EndpointPart } from '../../core/global';
import { EndpointService } from '../endpoint/endpoint.service';

import { User } from './model/user.model';
import { UserRights } from './user-rights.enum';
import { UserRole } from './user-role.enum';
import { UserTokenService } from './user-token.service';

@Injectable({
  providedIn: 'root',
})
export class UserService {
  private readonly userTokenService = inject(UserTokenService);
  private readonly sessionStorage = inject(SessionStorageService);
  private readonly http = inject(HttpClient);
  private readonly endpointService = inject(EndpointService);

  private user: User = new User(null, 'testID', [], [], 'en', null, null);

  public getUser(): User {
    return this.user ?? undefined;
  }

  public buildUserByToken(token: string, userLanguage: string): void {
    this.user = this.userTokenService.convertTokenToUser(token);
    if (this.user !== undefined && this.user !== null) {
      this.user.token = token;
      this.user.language = userLanguage;
      // this.getFullName(ppnUserId) method provides response to late
      // user is missing in session causing requests to fail with missing token
      // adding user to session without fullname
      this.addUserToSession(this.user);
    }
    lastValueFrom(this.getFullName(this.user.ppnUserId))
      .then(fullName => {
        this.user.setFullName(fullName);
        this.addUserToSession(this.user);
      })
      .catch(() => {
        // eslint-disable-next-line no-console
        console.log('User fullName not available');
        this.addUserToSession(this.user);
      });
  }

  private getFullName(ppnUserId: string): Observable<string> {
    const pathParams: Map<string, string> = new Map();
    pathParams.set('ppnUserId', ppnUserId);
    const url: string = this.endpointService.getEndpointUrl(
      completeEndpoint(EndpointPart.GET_USER_FULL_NAME),
      pathParams,
    );
    return this.http.get(url, { responseType: 'text' });
  }

  private addUserToSession(user: User): void {
    this.sessionStorage.store('user', user);
  }

  public getRole(): UserRole[] {
    return this.getUser().roles;
  }

  public getRights(): UserRights[] {
    return this.getUser().rights;
  }

  public logoutUser(): void {
    this.user = new User(null, 'testID', [], [], 'en', null, null);
    this.sessionStorage.clear('user');
  }

  public get userLanguage(): string {
    return this.user ? this.user.language : 'de';
  }

  public hasRight(userRight: UserRights): boolean {
    return this.getRights().includes(userRight);
  }

  public hasRights(userRights: UserRights[]): boolean {
    let hasRights = false;
    for (const right of userRights) {
      if (this.getRights().includes(right)) {
        hasRights = true;
      } else {
        return false;
      }
    }
    return hasRights;
  }
}
